// pushIdentity.js — what the app tells OneSignal at each auth transition
// (2026-11-03). Pure planner + a thin executor; tested in
// tests/unit/push-identity.test.mjs with a fake OneSignal.
//
// The alias (external_id) is deliberately STICKY: routine sign-out never calls
// logout(), because detaching the alias on every sign-out minted orphan
// identity records that absorbed every push (Sept 2026). Privacy is handled
// one level down instead: sign-out opts the device's push subscription OUT,
// so a signed-out phone stops receiving that account's pushes, and the next
// sign-in opts it back IN after the existing gated login(). logout() still
// runs only when a DIFFERENT account signs in on the device (the alias must
// move) and on account deletion.
//
// Events:
//   login           { prevAliased, userId, permissionGranted }
//                   -> [logout?] login [optIn?]   (logout only on account switch;
//                      optIn only once OS permission is granted, so it can
//                      never raise the native prompt)
//   sign_out        -> optOut                    (user initiated, alias kept)
//   account_delete  -> optOut logout
export function pushIdentitySteps(event, { prevAliased = null, userId = null, permissionGranted = false } = {}) {
  switch (event) {
    case 'login': {
      const uid = userId == null ? '' : String(userId);
      if (!uid) return [];
      const prev = prevAliased == null ? '' : String(prevAliased);
      const steps = [];
      if (prev && prev !== uid) steps.push('logout');
      steps.push('login');
      if (permissionGranted === true) steps.push('optIn');
      return steps;
    }
    case 'sign_out': return ['optOut'];
    case 'account_delete': return ['optOut', 'logout'];
    default: return [];
  }
}

/** Run the steps against the plugin object. Each step is isolated: one failing never skips the rest. Returns the steps attempted. */
export async function applyPushIdentity(OneSignal, event, ctx = {}) {
  const steps = pushIdentitySteps(event, ctx);
  for (const step of steps) {
    try {
      if (step === 'login') await OneSignal.login(String(ctx.userId));
      else if (step === 'logout') await OneSignal.logout();
      else if (step === 'optIn') await OneSignal.User?.pushSubscription?.optIn?.();
      else if (step === 'optOut') await OneSignal.User?.pushSubscription?.optOut?.();
    } catch (e) {
      try { console.log(`OneSignal ${step} error`, e); } catch {}
    }
  }
  return steps;
}
